/**
 * Severity carried by shape (DESIGN.md "Severity"): a 16px glyph, then the
 * word. Octagon critical (Coral, the only hue), triangle high (full ink),
 * diamond medium (secondary gray), square low (mid gray). The glyph is
 * decorative; the word is the accessible text, so it always renders.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { IconSeverityCrit, IconSeverityHigh, IconSeverityLow, IconSeverityMed, type IconComponent } from './icons'
import { SEVERITY_WORD } from './severity'
import type { SeverityLevel } from './types'

export interface SeverityGlyphProps {
  level: SeverityLevel
  /** The word after the glyph. Defaults to Critical / High / Medium / Low. */
  children?: ReactNode
  size?: 12 | 16
  className?: string
}

const SHAPE: Record<SeverityLevel, IconComponent> = {
  crit: IconSeverityCrit,
  high: IconSeverityHigh,
  med: IconSeverityMed,
  low: IconSeverityLow,
}

const TONE: Record<SeverityLevel, string> = {
  crit: 'text-sev-crit',
  high: 'text-sev-high',
  med: 'text-sev-med',
  low: 'text-sev-low',
}

export function SeverityGlyph({ level, children, size = 16, className }: SeverityGlyphProps) {
  const Shape = SHAPE[level]
  return (
    <span className={clsx('inline-flex items-center whitespace-nowrap text-text-primary', size === 12 ? 'gap-1' : 'gap-2', className)}>
      <Shape size={size} className={TONE[level]} />
      {children ?? SEVERITY_WORD[level]}
    </span>
  )
}

export default SeverityGlyph
